import ActiveToggleButton from "@/components/ActiveToggleButton";
import { BaseTable } from "@/components/BaseTable";
import CustomButton from "@/components/common/Button";
import EmailConfigurationService from "@/services/APIs/EmailConfigurationService";
import { tableName } from "@/utils/constant/tableName";
import navigationRoutes from "@/utils/navigationRoutes";
import { EmailConfiguration } from "@/utils/types/emailConfiguration";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const EmailConfigurationListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["emailConfigurations", page, pageSize],
    queryFn: () =>
      EmailConfigurationService.getAllEmailConfigurations({ page, pageSize }),
  });
  // const gridData = data ?? [];
  const total = data?.count ?? 0;

  const handleDelete = async (id: string) => {
    try {
      await EmailConfigurationService.deleteEmailConfiguration(id);
      refetch();
    } catch (error: any) {
      console.error("Failed to delete email configuration:", error);
    }
  };

  const columns: ProColumns<EmailConfiguration>[] = [
    {
      title: "SMTP Host",
      dataIndex: "smtp_host",
      key: "smtp_host",
      sorter: (a: any, b: any) => a.smtp_host!.localeCompare(b.smtp_host!),
    },

    {
      title: "Display Name",
      dataIndex: "display_name",
      key: "display_name",
      sorter: (a: any, b: any) =>
        a.display_name!.localeCompare(b.display_name!),
    },
    {
      title: "Reply To",
      dataIndex: "reply_to",
      key: "reply_to",
    },

    {
      title: "Is Active",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.email_configuration}
            pk={record.id}
            is_active={record.is_active}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() =>
            navigate(navigationRoutes.EMAIL_CONFIGURATION_EDIT(record.id))
          }
          type="text"
          title="Edit Email Configuration"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this email configuration?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete Email Configuration"
          />
        </Popconfirm>,
      ],
    },
  ];

  return (
    <BaseTable<EmailConfiguration>
      loading={isLoading}
      columns={columns}
      dataSource={data?.results ?? []}
      page={page}
      pageSize={pageSize}
      total={total}
      rowKey="id"
      toolBarRender={() => [
        <CustomButton
          key="add"
          type="primary"
          icon={<Plus size={16} />}
          onClick={() => navigate(navigationRoutes.EMAIL_CONFIGURATION_ADD)}
          title="Add New Email Configuration"
        >
          Add Email Configuration
        </CustomButton>,
      ]}
      headerTitle="Email Configuration List"
      onPageChange={(page, size) => {
        setPage(page);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", size.toString());
        navigate(
          `${navigationRoutes.EMAIL_CONFIGURATION}?${searchParams.toString()}`
        );
        setPageSize(size ?? 10);
      }}
      request={() => refetch()}
    />
  );
};

export default EmailConfigurationListPage;
